/* eslint-disable import/no-restricted-paths */

import type { FAnonymousType104 } from '@/src/api/models/FAnonymousType104'
import type { FAnonymousType124 } from '@/src/api/models/FAnonymousType124'
import type { FAnonymousType155 } from '@/src/api/models/FAnonymousType155'
import type { MeasurementDTO } from '@/src/api/models/MeasurementDTO'
import type { HttpHandler } from 'msw'
import { Asset } from 'expo-asset'
import { http, HttpResponse } from 'msw'

const sessionsPath = '*/api/session'
const sessionByIdPath = '*/api/session/:id'
const measurementsPath = '*/api/measurement'
const measurementByIdPath = '*/api/measurement/:id'
const measurementRecordingPath = '*/api/measurement/:id/recording'
const MOCK_RECORDING_AUDIO_SOURCE = require('../../../assets/audio/voice-assessment-example.wav')

type SessionListMockItem = Omit<FAnonymousType124, 'created'> & {
  // MSW returns raw JSON; generated client parses this into `Date`.
  created: string
}

type SessionByIdMockResponse = Omit<FAnonymousType155, 'created'> & {
  // MSW returns raw JSON; generated client parses this into `Date`.
  created: string
}

const sessionListMockResponse: SessionListMockItem[] = [
  {
    id: 1,
    definition: {
      id: 1,
      name: 'Mock PendingAnalysis session',
    },
    status: 'PendingAnalysis',
    created: '2026-05-10T21:00:00.000Z',
  },
  {
    id: 2,
    definition: {
      id: 1,
      name: 'Session A',
    },
    status: 'Aborted',
    created: '2026-05-09T21:01:00.000Z',
  },
  {
    id: 3,
    definition: {
      id: 1,
      name: 'Session A',
    },
    status: 'Active',
    created: '2026-05-08T21:02:00.000Z',
  },
  {
    id: 4,
    definition: {
      id: 1,
      name: 'Session A',
    },
    status: 'Completed',
    created: '2026-05-07T21:03:00.000Z',
  },
]

const sessionByIdMockResponse: SessionByIdMockResponse = {
  id: 4,
  definition: {
    id: 1,
    name: 'Session A',
  },
  status: 'Completed',
  created: '2026-05-07T21:03:00.000Z',
  measurements: [
    {
      id: 43,
      definition: {
        id: 7,
        name: 'Check In"',
      },
    },
    {
      id: 44,
      definition: {
        id: 9,
        name: 'Measurement A',
      },
    },
  ],
}

const measurementListMockResponse: FAnonymousType104[] = [
  {
    id: 43,
    definition: {
      id: 7,
      name: 'Check In"',
    },
  },
  {
    id: 44,
    definition: {
      id: 9,
      name: 'Measurement A',
    },
  },
]

const measurement43MockResponse: MeasurementDTO = {
  sessionId: 4,
  definitionId: 7,
  answers: [
    {
      questionId: 1,
      choiceIndices: [1],
    },
    {
      questionId: 2,
      choiceIndices: [2],
    },
  ],
}

const measurement44MockResponse: MeasurementDTO = {
  sessionId: 4,
  definitionId: 9,
  answers: [
    {
      questionId: 1,
      choiceIndices: [1],
    },
    {
      questionId: 2,
      choiceIndices: [2],
    },
    {
      questionId: 3,
      choiceIndices: [3],
    },
    {
      questionId: 4,
      choiceIndices: [4],
    },
  ],
}

function mockSessions(response: SessionListMockItem[]): HttpHandler {
  return http.get(sessionsPath, () =>
    HttpResponse.json(response, { status: 200 }),
  )
}

function mockSessionById(response: SessionByIdMockResponse): HttpHandler {
  return http.get(sessionByIdPath, () =>
    HttpResponse.json(response, { status: 200 }),
  )
}

function mockMeasurements(response: FAnonymousType104[]): HttpHandler {
  return http.get(measurementsPath, () =>
    HttpResponse.json(response, { status: 200 }),
  )
}

function mockMeasurementById(): HttpHandler {
  return http.get(measurementByIdPath, ({ params }) => {
    if (params.id === '43') {
      return HttpResponse.json(measurement43MockResponse, { status: 200 })
    }
    if (params.id === '44') {
      return HttpResponse.json(measurement44MockResponse, { status: 200 })
    }
    return new HttpResponse(null, { status: 404 })
  })
}

function mockMeasurementRecording(): HttpHandler {
  return http.get(measurementRecordingPath, async ({ params }) => {
    if (params.id !== '44') {
      return new HttpResponse(null, { status: 404 })
    }

    const asset = Asset.fromModule(MOCK_RECORDING_AUDIO_SOURCE)
    const response = await fetch(asset.uri)
    const audioBuffer = await response.arrayBuffer()

    return HttpResponse.arrayBuffer(audioBuffer, {
      headers: {
        'content-type': 'audio/wav',
      },
    })
  })
}

export const sessionHandlers: HttpHandler[] = [
  mockSessions(sessionListMockResponse),
  mockSessionById(sessionByIdMockResponse),
  mockMeasurements(measurementListMockResponse),
  mockMeasurementById(),
  mockMeasurementRecording(),
]
